import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import {
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  makeStyles,
} from "@material-ui/core";
import { green } from "@material-ui/core/colors";
import { Colorize } from "@material-ui/icons";

import ColorPicker, { isValidHexColor, normalizeHexColor, saveCustomColor } from "../ColorPicker";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
  textField: {
    marginRight: theme.spacing(1),
    flex: 1,
  },
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  colorAdornment: {
    width: 20,
    height: 20,
    borderRadius: 4,
    border: "1px solid rgba(0, 0, 0, 0.18)",
  },
  formControl: {
    width: "100%",
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
  },
}));

const LabelSchema = Yup.object().shape({
  name: Yup.string().min(2, "Muy corto").max(50, "Muy largo").required("Requerido"),
  color: Yup.string().min(3, "Muy corto").max(9, "Muy largo").required("Requerido"),
  sharedConnectionIds: Yup.array().min(1, "Requerido"),
});

const initialState = {
  name: "",
  color: "#2f80ed",
  connectionId: "",
  sharedConnectionIds: [],
};

const normalizeConnectionIds = (...values) => {
  const ids = new Set();
  values.forEach(value => {
    if (!value) return;
    if (Array.isArray(value)) {
      value.forEach(id => {
        if (id) ids.add(Number(id));
      });
      return;
    }
    ids.add(Number(value));
  });
  return Array.from(ids).filter(id => !Number.isNaN(id));
};

const getWhatsappName = (connections, connectionId) =>
  connections.find(whatsapp => Number(whatsapp.id) === Number(connectionId))?.name ||
  connections.find(whatsapp => Number(whatsapp.id) === Number(connectionId))?.number ||
  `Conexión ${connectionId}`;

const LabelModal = ({ open, onClose, labelId, defaultConnectionId }) => {
  const classes = useStyles();
  const [label, setLabel] = useState(initialState);
  const [connections, setConnections] = useState([]);
  const [colorPickerModalOpen, setColorPickerModalOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data: connectionData } = await api.get("/connections/");
        setConnections(connectionData || []);
      } catch (err) {
        toastError(err);
      }

      if (!labelId) {
        const sharedConnectionIds = normalizeConnectionIds(defaultConnectionId);
        setLabel({
          ...initialState,
          connectionId: defaultConnectionId || "",
          sharedConnectionIds,
        });
        return;
      }

      try {
        const { data } = await api.get(`/labels/${labelId}`);
        setLabel(prevState => ({
          ...prevState,
          ...data,
          connectionId: data.connectionId || defaultConnectionId || "",
          sharedConnectionIds: normalizeConnectionIds(
            data.sharedConnectionIds,
            data.connectionId,
            defaultConnectionId
          ),
        }));
      } catch (err) {
        toastError(err);
      }
    })();

    return () => setLabel(initialState);
  }, [labelId, open, defaultConnectionId]);

  const handleClose = () => {
    onClose();
    setLabel(initialState);
  };

  const handleSaveLabel = async values => {
    try {
      if (isValidHexColor(values.color)) {
        saveCustomColor(values.color);
      }
      const sharedConnectionIds = normalizeConnectionIds(
        values.sharedConnectionIds,
        values.connectionId
      );
      const payload = {
        ...values,
        connectionId: values.connectionId || sharedConnectionIds[0] || "",
        sharedConnectionIds,
        color: normalizeHexColor(values.color),
      };
      if (labelId) {
        await api.put(`/labels/${labelId}`, payload);
      } else {
        await api.post("/labels", payload);
      }
      toast.success(i18n.t("labelModal.success"));
      handleClose();
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} scroll="paper">
      <DialogTitle>
        {labelId ? i18n.t("labelModal.title.edit") : i18n.t("labelModal.title.add")}
      </DialogTitle>
      <Formik
        initialValues={label}
        enableReinitialize
        validationSchema={LabelSchema}
        onSubmit={(values, actions) => {
          handleSaveLabel(values).finally(() => actions.setSubmitting(false));
        }}
      >
        {({ touched, errors, isSubmitting, values, setFieldValue, handleBlur }) => (
          <Form>
            <DialogContent dividers>
              <Field
                as={TextField}
                label={i18n.t("labelModal.form.name")}
                autoFocus
                name="name"
                error={touched.name && Boolean(errors.name)}
                helperText={touched.name && errors.name}
                variant="outlined"
                margin="dense"
                className={classes.textField}
              />
              <FormControl variant="outlined" className={classes.formControl}>
                <InputLabel>Conexiones</InputLabel>
                <Select
                  multiple
                  value={values.sharedConnectionIds || []}
                  label="Conexiones"
                  renderValue={selected =>
                    selected.map(id => getWhatsappName(connections, id)).join(", ")
                  }
                  onChange={event => {
                    const selectedIds = event.target.value;
                    setFieldValue("sharedConnectionIds", selectedIds);
                    setFieldValue(
                      "connectionId",
                      selectedIds.includes(Number(values.connectionId))
                        ? values.connectionId
                        : selectedIds[0] || ""
                    );
                  }}
                >
                  {connections.map(whatsapp => (
                    <MenuItem key={whatsapp.id} value={whatsapp.id}>
                      <Checkbox checked={(values.sharedConnectionIds || []).indexOf(whatsapp.id) > -1} />
                      <ListItemText
                        primary={whatsapp.name || whatsapp.number || `Conexión ${whatsapp.id}`}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Field
                as={TextField}
                label={i18n.t("labelModal.form.color")}
                name="color"
                onBlur={event => {
                  handleBlur(event);
                  if (isValidHexColor(event.target.value)) {
                    const color = normalizeHexColor(event.target.value);
                    setFieldValue("color", color);
                    saveCustomColor(color);
                  }
                }}
                error={touched.color && Boolean(errors.color)}
                helperText={touched.color && errors.color}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <div
                        style={{ backgroundColor: values.color }}
                        className={classes.colorAdornment}
                      />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <IconButton
                      size="small"
                      color="default"
                      onClick={() => setColorPickerModalOpen(true)}
                    >
                      <Colorize />
                    </IconButton>
                  ),
                }}
                variant="outlined"
                margin="dense"
              />
              <ColorPicker
                open={colorPickerModalOpen}
                currentColor={values.color}
                handleClose={() => setColorPickerModalOpen(false)}
                onChange={color => {
                  setFieldValue("color", color);
                  setLabel({ ...values, color });
                }}
              />
            </DialogContent>
            <DialogActions>
              <Button
                onClick={handleClose}
                color="secondary"
                disabled={isSubmitting}
                variant="outlined"
              >
                {i18n.t("labelModal.buttons.cancel")}
              </Button>
              <Button
                type="submit"
                color="primary"
                disabled={isSubmitting}
                variant="contained"
                className={classes.btnWrapper}
              >
                {labelId ? i18n.t("labelModal.buttons.okEdit") : i18n.t("labelModal.buttons.okAdd")}
                {isSubmitting && <CircularProgress size={24} className={classes.buttonProgress} />}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default LabelModal;

